import type {StyleFunctionProps} from '@chakra-ui/styled-system';
import {createMultiStyleConfigHelpers} from '@chakra-ui/styled-system';
import {mode} from '@chakra-ui/theme-tools';

import {core as coreDark, semantic as semanticDark} from '../../../design-tokens/dark';
import {core, semantic} from '../../../design-tokens/light';
import {getCoreValueAsThemeToken} from '../../types';

import {anatomy} from './anatomy';

const {definePartsStyle} = createMultiStyleConfigHelpers(anatomy);

type StyleFunctionPropsWithSize = StyleFunctionProps & {size: 'lg' | `md`};
const sizes = {
  lg: definePartsStyle({
    tablist: {
      h: `48px`,
      gap: getCoreValueAsThemeToken(core, `space.space-100`),
      p: getCoreValueAsThemeToken(core, `space.space-050`),
    },
    tab: {
      h: `40px`,
      px: getCoreValueAsThemeToken(core, `space.space-200`),
      py: getCoreValueAsThemeToken(core, `space.space-200`),
    },
    indicator: {
      h: `40px`,
      px: getCoreValueAsThemeToken(core, `space.space-200`),
      py: getCoreValueAsThemeToken(core, `space.space-200`),
    },
  }),
  md: definePartsStyle({
    tablist: {
      h: `40px`,
      gap: getCoreValueAsThemeToken(core, `space.space-200`),
      p: getCoreValueAsThemeToken(core, `space.space-050`),
    },
    tab: {
      h: `32px`,

      px: getCoreValueAsThemeToken(core, `space.space-100`),
      py: getCoreValueAsThemeToken(core, `space.space-200`),
    },
    indicator: {
      h: `32px`,
      px: getCoreValueAsThemeToken(core, `space.space-100`),
      py: getCoreValueAsThemeToken(core, `space.space-200`),
    },
  }),
};

export const fill = definePartsStyle(props => {
  const {size} = props as StyleFunctionPropsWithSize;
  const sizeProps = sizes?.[size] ?? sizes.lg;
  return {
    tablist: {
      ...sizeProps.tablist,
      borderRadius: getCoreValueAsThemeToken(core, `radii.radius-1000`),
      border: `1px solid`,
      borderColor: mode(core.colors.obsidian[200], coreDark.colors.obsidian[200])(props),
      bg: mode(core.colors.obsidian[100], coreDark.colors.obsidian[100])(props),
      alignItems: `center`,
    },

    tab: {
      ...sizeProps.tab,
      display: `flex`,
      alignItems: `center`,
      justifyContent: `center`,
      gap: getCoreValueAsThemeToken(core, `space.space-100`),
      borderRadius: getCoreValueAsThemeToken(core, `radii.radius-1000`),
      color: mode(semantic.colors.text.primary, semanticDark.colors.text.primary)(props),
      boxShadow: `none`,
      bg: `transparent`,
      zIndex: 1,
      flexShrink: 0,
    },
    badge: {
      display: `inline-flex`,
      // TODO: move to sizes
      height: `32px`,
      padding: getCoreValueAsThemeToken(core, `space.space-050`),
      justifyContent: `center`,
      alignItems: `center`,
      maxHeight: `2em`,
      minWidth: `2em`,
      gap: getCoreValueAsThemeToken(core, `space.space-100`),
      flexShrink: 0,
      borderRadius: getCoreValueAsThemeToken(core, `radii.radius-rounded`),
      bg: mode(semantic.colors.icon[`brand-subtle`], semanticDark.colors.icon[`brand-subtle`])(props),
      color: mode(semantic.colors.icon[`brand-bold`], semanticDark.colors.icon[`brand-bold`])(props),
    },
    indicator: {
      ...sizeProps.indicator,
      flexShrink: 0,
      borderRadius: getCoreValueAsThemeToken(core, `radii.radius-1000`),
      boxShadow: `xs`,
      bg: mode(semantic.colors.background[`elevation-3`], semanticDark.colors.background[`elevation-3`])(props),
      marginTop: `-1px`,
    },
  };
});
